"use client";
import React, { Fragment, useState } from "react";
import { useMutation } from "@apollo/client/react";
import { faCheck, faEdit, faPlusCircle, faTrashAlt } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { ReactionsBreakdown } from "@/app/(content)/animation/[slug]/reactions-breakdown";
import {
  Button,
  DropdownMenu,
  IfUserIs,
  PublicGallerySwitch,
  Reactions,
  ReactionSelector,
  Thread,
} from "@/components";
import { Module } from "@/components/layouts";
import ConfirmAnimationDeleteModal from "@/components/modal/components/confirm-animation-delete";
import LoadAnimationModal from "@/components/modal/components/load-animation";
import { GetComicQuery } from "@/gql/generated/graphql";
import { MUTATE_ANIMATION } from "@/gql/queries/mutate-animation";
import { MUTATE_COMIC } from "@/gql/queries/mutations/mutate-comic";
import { useModal } from "@/hooks/use-modal";

import ComicFrame from "./comic-frame";

const colsClassMap: Record<number, string> = {
  1: "md:grid-cols-1",
  2: "md:grid-cols-2",
  3: "md:grid-cols-3",
  4: "md:grid-cols-4",
};

const ComicPage = ({
  comicData,
}: {
  comicData: NonNullable<GetComicQuery["comic"]>;
}) => {
  const { openModal } = useModal();
  const [mutateComic] = useMutation(MUTATE_COMIC);
  const [mutateAnimation] = useMutation(MUTATE_ANIMATION);
  const [editMode, setEditMode] = useState(false);

  const onAddAnimationPressed = () => {
    openModal(() => (
      <LoadAnimationModal
        onSelected={(animationUrl: string) => {
          mutateComic({
            variables: {
              comicId: comicData.id,
              addAnimationUrl: animationUrl,
            },
          });
        }}
      />
    ));
  };

  const onSetColumnsPressed = (numCols: number) => {
    mutateComic({
      variables: {
        comicId: comicData.id,
        columns: numCols,
      },
    });
  };

  const onAnimationPublicToggled = () => {
    mutateAnimation({
      variables: {
        id: comicData.parentAnimation.id,
        public: !comicData.parentAnimation.public,
      },
    });
  };

  const onDeleteComicPressed = () => {
    openModal(() => (
      <ConfirmAnimationDeleteModal id={comicData.parentAnimation.id} />
    ));
  };

  return (
    <>
      <IfUserIs userId={comicData.user.id}>
        {!editMode && (
          <Module>
            <Button
              type="positive"
              onClick={() => {
                setEditMode(true);
              }}
            >
              <FontAwesomeIcon icon={faEdit} /> Edit Comic
            </Button>
          </Module>
        )}
        {editMode && (
          <Module>
            <Button
              type="positive"
              onClick={() => {
                setEditMode(false);
              }}
            >
              <FontAwesomeIcon icon={faEdit} /> Finish Editing
            </Button>
            <PublicGallerySwitch
              onAnimationPublicToggled={onAnimationPublicToggled}
              isPublic={comicData.parentAnimation.public}
            />
            <DropdownMenu
              button={{ text: "Comic layout" }}
              items={[
                {
                  text: (
                    <span>
                      1 col{" "}
                      {comicData.columns === 1 && (
                        <FontAwesomeIcon icon={faCheck} />
                      )}
                    </span>
                  ),
                  onClick: () => onSetColumnsPressed(1),
                },
                {
                  text: (
                    <span>
                      2 cols{" "}
                      {comicData.columns === 2 && (
                        <FontAwesomeIcon icon={faCheck} />
                      )}
                    </span>
                  ),
                  onClick: () => onSetColumnsPressed(2),
                },
                {
                  text: (
                    <span>
                      3 cols{" "}
                      {comicData.columns === 3 && (
                        <FontAwesomeIcon icon={faCheck} />
                      )}
                    </span>
                  ),
                  onClick: () => onSetColumnsPressed(3),
                },
                {
                  text: (
                    <span>
                      4 cols{" "}
                      {comicData.columns === 4 && (
                        <FontAwesomeIcon icon={faCheck} />
                      )}
                    </span>
                  ),
                  onClick: () => onSetColumnsPressed(4),
                },
              ]}
            />{" "}
            <Button type="negative" onClick={onDeleteComicPressed}>
              <FontAwesomeIcon icon={faTrashAlt} /> Delete Comic
            </Button>
          </Module>
        )}
      </IfUserIs>
      <Module>
        <div
          className={`w-full grid gap-2 items-center grid-cols-1 ${colsClassMap[comicData.columns]
            }`}
        >
          {comicData.frames.map((frame) => {
            return (
              <ComicFrame
                comic={comicData}
                key={frame.id}
                frame={frame}
                numFrames={comicData.frames.length}
                editMode={editMode}
              />
            );
          })}
          {editMode && (
            <IfUserIs userId={comicData.user.id}>
              <div className="bg-blue-50 border-2 border-gray-400 border-dotted text-center w-full h-full flex items-center justify-center p-5">
                <Button type="positive" onClick={onAddAnimationPressed}>
                  <FontAwesomeIcon icon={faPlusCircle} /> Add animation
                </Button>
              </div>
            </IfUserIs>
          )}
        </div>
      </Module>
      {!editMode && (
        <Fragment>
          <IfUserIs not userId={comicData.user.id}>
            <Module>
              <ReactionSelector
                animationId={comicData.parentAnimation.id}
                reactionsBreakdown={
                  comicData.parentAnimation.reactionsBreakdown
                }
              />
            </Module>
          </IfUserIs>
          <Module>
            <div className="animation-page__reactions">
              <Reactions
                reactions={comicData.parentAnimation.reactions}
                type="large"
              />
            </div>
          </Module>
          <Module>
            <ReactionsBreakdown
              reactions={comicData.parentAnimation.reactionsBreakdown}
            />
          </Module>
          <Thread
            thread={comicData.parentAnimation.thread}
            animation={comicData.parentAnimation}
          />
        </Fragment>
      )}
    </>
  );
};

export default ComicPage;
